import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { setupPageCountKey, setupCouponExistsKey } from '@dashboard/lib/queryKeys';

// The page-count and coupon steps are the only ones gated on a network call.
// We cache their last-known result per seller so that on reload we can render
// the real completion state immediately (queries revalidate silently) instead
// of guessing — which is what caused the progress bar to tick backwards.
const readCachedDone = key => {
  if (typeof window === 'undefined') return null;
  const value = localStorage.getItem(key);
  return value === null ? null : value === 'true';
};

const writeCachedDone = (key, done) => {
  if (typeof window !== 'undefined') localStorage.setItem(key, String(done));
};

export default function useSetupSteps() {
  const { seller, token, isAuthenticating } = useAuth();
  const sellerId = seller?._id;

  const pageDoneKey = `setup:pageDone:${sellerId}`;
  const couponDoneKey = `setup:couponDone:${sellerId}`;

  const { data: pageCount, isLoading: isLoadingPages } = useQuery({
    queryKey: setupPageCountKey(sellerId),
    queryFn: async () => {
      const count = await fetcher(`/sellers/${sellerId}/pages/count`, token);
      writeCachedDone(pageDoneKey, count > 0);
      return count;
    },
    enabled: !!sellerId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  const { data: couponData, isLoading: isLoadingCoupons } = useQuery({
    queryKey: setupCouponExistsKey(sellerId),
    queryFn: async () => {
      const coupons = await fetcher(`/sellers/${sellerId}/coupons?limit=1`, token);
      const count = Array.isArray(coupons) ? coupons.length : (coupons?.length ?? 0);
      writeCachedDone(couponDoneKey, count > 0);
      return coupons;
    },
    enabled: !!sellerId && !!token,
    staleTime: 5 * 60 * 1000,
  });

  const couponCount = Array.isArray(couponData) ? couponData.length : (couponData?.length ?? 0);

  // Fall back to the cached value while the query is in flight. When there's no
  // cache yet (brand-new seller's first load) an unknown step counts as not
  // done — accurate for a new account, and it never ticks backwards.
  const cachedPageDone = readCachedDone(pageDoneKey);
  const cachedCouponDone = readCachedDone(couponDoneKey);
  const pageDone = pageCount !== undefined ? pageCount > 0 : (cachedPageDone ?? false);
  const couponDone = couponData !== undefined ? couponCount > 0 : (cachedCouponDone ?? false);
  const pageChecking = isLoadingPages && cachedPageDone === null;
  const couponChecking = isLoadingCoupons && cachedCouponDone === null;

  const customerPortalVisited =
    typeof window !== 'undefined' && localStorage.getItem('customerPortalVisited') === 'true';

  const steps = [
    {
      title: 'Create your first page',
      description: 'Sell products, subscriptions & event tickets.',
      action: 'Create page',
      link: '/pages/new',
      completed: pageDone,
      loading: pageChecking,
    },
    {
      title: 'Connect Stripe account',
      description: 'Start accepting payments securely.',
      action: 'Connect Stripe',
      link: '/settings/stripe',
      completed: checkIsConnectedToStripe(seller),
    },
    {
      title: 'Add your branding',
      description: 'Upload your logo and customize colors.',
      action: 'Customize',
      link: '/settings/general',
      completed: !!seller?.logo,
    },
    {
      title: 'Set up public details',
      description: 'Add your business info for invoices and receipts.',
      action: 'Set up',
      link: '/settings/public-details',
      completed: !!seller?.supportEmail && !!seller?.businessProfile?.businessName && !!seller?.url,
    },
    {
      title: 'Add a custom domain',
      description: 'Use your own domain for checkout pages.',
      action: 'Add custom domain',
      link: '/settings/domains',
      completed: !!seller?.customDomain && !!seller?.customDomainVerified,
    },
    {
      title: 'Add email domain',
      description: 'Send confirmation emails from your own domain.',
      action: 'Add email domain',
      link: '/settings/email',
      completed:
        !!seller?.emailDomain?.dkimVerified && !!seller?.emailDomain?.returnPathDomainVerified,
    },
    {
      title: 'Enable cart abandonment',
      description: 'Recover lost sales with reminder emails.',
      action: 'Set up',
      link: '/settings/cart-abandonment',
      completed: !!seller?.checkoutAbandonment?.collectEmails,
    },
    {
      title: 'Invite team members',
      description: 'Collaborate with your team.',
      action: 'Invite team members',
      link: '/settings/members',
      completed: seller?.invites?.length > 0,
      optional: true,
    },
    {
      title: 'Create a coupon',
      description: 'Offer discounts to your customers.',
      action: 'Create coupon',
      link: '/coupons/new',
      completed: couponDone,
      loading: couponChecking,
    },
    {
      title: 'Set up tax collection',
      description: 'Collect taxes automatically based on your customers’ location.',
      action: 'Set up tax collection',
      link: '/settings/tax',
      completed: !!seller?.tax?.mode, // considered completed if tax collection has been touched, even if it's set to No tax
    },
    {
      title: 'Visit customer portal',
      description: 'See how customers manage their purchases.',
      action: 'Visit customer portal',
      link: '/settings/customer-portal',
      completed: customerPortalVisited,
      onClick: () => localStorage.setItem('customerPortalVisited', 'true'),
    },
  ];

  // Render as soon as auth resolves; the network-backed steps are seeded from
  // their cached completion above, so the count is stable across reloads.
  const requiredSteps = steps.filter(s => !s.optional);
  const completedCount = requiredSteps.filter(s => s.completed).length;
  const totalCount = requiredSteps.length;
  const allCompleted = completedCount === totalCount;
  const isLoading = isAuthenticating;

  return { steps, completedCount, totalCount, allCompleted, isLoading };
}
